import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import LoadingComponent from "../../components/loading-component";
import { NumberDropDown } from "../../components/NumberDropdown";
import { YesNoComponent } from "../../components/yesno-component";
import ToastHelpers from "../../services/core/toast-helpers";
import Member from "../../services/member/member";
import memberService from "../../services/member/member-service";
import micrositeService from "../../services/micro-site/microsite-service";
import useCompartmentSizes from "../../services/hub/useCompartmentSizes";
import { CompartmentSize } from "../../services/hub/compartmentSize";
import languageService from "../../services/core/language-service";
import "./event-page.scss";

interface Props {
  member: Member | undefined;
}

interface IParamTypes {
  organizationId: string;
  micrositeId: string;
  eventId: string;
}

export default function EventPage(props: Props) {
  const { t } = useTranslation();
  const { member } = props;
  const { organizationId, micrositeId, eventId } = useParams<IParamTypes>();
  const language: string = languageService.getLanguage();

  const { compartmentSizes } = useCompartmentSizes({ onErrorNotificationMessage: t("event-page.event-page-error-refresh") });
  const [micrositeCompartmentSizes, setMicrositeCompartmentSizes] = useState<CompartmentSize[] | undefined>(undefined);

  const [loading, setLoading] = useState<boolean>(true);
  const [isDeleteReservationBlock, setIsDeleteReservationBlock] = useState<boolean>(false);
  const [eventAlreadyStarted, setEventAlreadyStarted] = useState<boolean>(false);

  const [nameFrError, setNameFrError] = useState<boolean>(false);
  const [nameEnError, setNameEnError] = useState<boolean>(false);
  const [priceError, setPriceError] = useState<boolean>(false);
  const [saleTaxRateError, setSaleTaxError] = useState<boolean>(false);
  const [durationError, setDurationError] = useState<boolean>(false);
  const [availableStartError, setAvailableStartError] = useState<string | undefined>(undefined);
  const [availableEndError, setAvailableEndError] = useState<string | undefined>(undefined);
  const [endOnError, setEndOnError] = useState<string | undefined>(undefined);
  const [startOnError, setStartOnError] = useState<string | undefined>(undefined);

  const [nameFr, setNameFr] = useState<string | undefined>(undefined);
  const [nameEn, setNameEn] = useState<string | undefined>(undefined);
  const [descriptionFr, setDescriptionFr] = useState<string | undefined>(undefined);
  const [descriptionEn, setDescriptionEn] = useState<string | undefined>(undefined);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [price, setPrice] = useState<number>(0);
  const [saleTaxRate, setSaleTaxRate] = useState<number>(0);
  const [startOn, setStartOn] = useState<string | undefined>(undefined);
  const [availableStart, setAvailableStart] = useState<string | undefined>(undefined);
  const [availableEnd, setAvailableEnd] = useState<string | undefined>(undefined);
  const [endOn, setEndOn] = useState<string | undefined>(undefined);
  const [sizeId, setSizeId] = useState<string | undefined>(undefined);

  const [isCodeSpecified, setIsCodeSpecified] = useState<boolean>(false);
  const [isDurationInput, setIsDurationInput] = useState<boolean>(true);
  const [isFixedPeriod, setIsFixedPeriod] = useState<boolean>(false);
  const [durationDays, setDurationDays] = useState<number>(0);
  const [durationHours, setDurationHours] = useState<number>(0);
  const [durationMinutes, setDurationMinutes] = useState<number>(0);

  const dateTimeNow = () => moment(new Date()).format("YYYY-MM-DDTHH:mm");

  const history = useHistory();

  useEffect(
    () => {
      if (compartmentSizes.length > 0) {
        let organization = member && member.associatedOrganizations.find((organization) => organization.entity?.id === organizationId);
        if (member) {
          if (organization && memberService.isOrganizationContributor(member, organizationId)) {
            micrositeService
              .getMicrosite(micrositeId, "All")
              .then((microsite) => {
                if (microsite) {
                  micrositeService
                    .getMicrositeSizesIds(microsite)
                    .then((sizeIds) => {
                      let tmpCompartmentSizes = compartmentSizes.filter((size) => sizeIds.has(size.id));
                      setMicrositeCompartmentSizes(tmpCompartmentSizes);
                    })
                    .catch(() => {
                      ToastHelpers.error(t("event-page.event-page-error-refresh"));
                    })
                    .finally(() => {
                      setLoading(false);
                    });

                  const reservationBlock = microsite.reservationBlocks.find((rb) => rb.id === eventId);
                  if (reservationBlock && eventId !== "new") {
                    setNameFr(reservationBlock.name.get("fr"));
                    setNameEn(reservationBlock.name.get("en"));
                    setDescriptionFr(reservationBlock.description.get("fr"));
                    setDescriptionEn(reservationBlock.description.get("en"));
                    setLogoUrl(reservationBlock.logoUrl);
                    setPrice(reservationBlock.price);
                    setSaleTaxRate(reservationBlock.saleTaxRate * 100);
                    setSizeId(reservationBlock.sizeId);
                    setIsCodeSpecified(reservationBlock.codeSpecified);

                    if (reservationBlock.startOn && reservationBlock.availableEnd) {
                      setStartOn(moment(new Date(reservationBlock.startOn)).format("YYYY-MM-DDTHH:mm"));
                      setEndOn(moment(new Date(reservationBlock.availableEnd)).format("YYYY-MM-DDTHH:mm"));
                      setIsDurationInput(false);
                    }

                    //Get the date and put it in the selected options
                    if (reservationBlock.startOn && reservationBlock.availableEnd && reservationBlock.availableStart) {
                      setAvailableStart(moment(new Date(reservationBlock.availableStart)).format("YYYY-MM-DDTHH:mm"));

                      if (dateTimeNow() > moment(new Date(reservationBlock.startOn)).format("YYYY-MM-DDTHH:mm"))
                        setEventAlreadyStarted(true);

                      setDurationDays(0);
                      setDurationHours(0);
                      setDurationMinutes(0);
                    } else {
                      if (reservationBlock.availableStart && reservationBlock.availableEnd) {
                        setIsFixedPeriod(true);

                        if (dateTimeNow() > moment(new Date(reservationBlock.availableStart)).format("YYYY-MM-DDTHH:mm"))
                          setEventAlreadyStarted(true);

                        setAvailableStart(moment(new Date(reservationBlock.availableStart)).format("YYYY-MM-DDTHH:mm"));
                        setAvailableEnd(moment(new Date(reservationBlock.availableEnd)).format("YYYY-MM-DDTHH:mm"));

                        let end: Date = new Date(reservationBlock.availableEnd);
                        let start: Date = new Date(reservationBlock.availableStart);

                        setDurationDays(end.getDay() - start.getDay());
                        setDurationHours(end.getHours() - start.getHours());
                        setDurationMinutes(end.getMinutes() - start.getMinutes());
                      } else if (!reservationBlock.startOn) {
                        setAvailableStart(undefined);
                        setAvailableEnd(undefined);

                        let Hours_Minutes = [""];
                        if (reservationBlock.duration.toString().includes(".")) {
                          let days = reservationBlock.duration.toString().split(".");
                          setDurationDays(parseInt(days[0]));
                          Hours_Minutes = days[1].split(":");
                        } else Hours_Minutes = reservationBlock.duration.toString().split(":");

                        setDurationHours(parseInt(Hours_Minutes[0]));
                        setDurationMinutes(parseInt(Hours_Minutes[1]));
                      }
                    }
                  }
                }
              })
              .catch(() => {
                ToastHelpers.error(t("event-page.event-page-error-refresh"));
                setLoading(false);
              });
          }
        }
      }
    },
    // eslint-disable-next-line
    [compartmentSizes]
  );

  if (isDeleteReservationBlock) {
    return (
      <div className="container page orders-page">
        <YesNoComponent
          headerMsg={t("event-page.event-page-msg-delete-reservationBlock")}
          onYesClick={() => deleteReservationBlock()}
          onNoClick={setIsDeleteReservationBlock}
        />
      </div>
    );
  }

  return (
    <div className="container page orders-page">
      <div className="col col-lg-6 offset-lg-3">
        {eventId === "new" && <h1>{t("event-page.event-page-create-title")}</h1>}
        {eventId !== "new" && <h1>{t("event-page.event-page-edit-title")}</h1>}

        <LoadingComponent loading={loading} />

        {!loading && (
          <div className="customer card">
            <div className="card-header align-items-center">
              <div className="d-inline-block">
                <h2>{t("event-page.event-page-information")}</h2>
              </div>
              <button
                className="btn-close float-end"
                onClick={() => history.push(`/organizations/${organizationId}/microsites/${micrositeId}/reservationBlocks`)}
              ></button>
            </div>

            <div className="card-body">
              <form onSubmit={(ev) => ev.preventDefault()}>
                {/* Name Fr */}
                <div className="mb-3">
                  <label className="form-label">{t("event-page.event-page-create-nameFr")}</label>
                  <input
                    className="form-control"
                    value={nameFr}
                    onChange={(ev) => onNameChange(ev.target.value, setNameFr, setNameFrError)}
                    required
                    autoFocus={true}
                    disabled={eventAlreadyStarted}
                  />
                  {nameFrError && (
                    <label className="error-msg" id="nameFr-Error-Msg">
                      {t("event-page.event-page-error-nameFr")}
                    </label>
                  )}
                </div>

                {/* Name En */}
                <div className="mb-3">
                  <label className="form-label">{t("event-page.event-page-create-nameEn")}</label>
                  <input
                    className="form-control"
                    value={nameEn}
                    onChange={(ev) => onNameChange(ev.target.value, setNameEn, setNameEnError)}
                    required
                    disabled={eventAlreadyStarted}
                  />
                  {nameEnError && (
                    <label className="error-msg" id="nameEn-Error-Msg">
                      {t("event-page.event-page-error-nameEn")}
                    </label>
                  )}
                </div>

                {/* Description Fr */}
                <div className="mb-3">
                  <label className="form-label">{t("event-page.event-page-create-descriptionFr")}</label>
                  <input
                    className="form-control"
                    value={descriptionFr}
                    onChange={(ev) => setDescriptionFr(ev.target.value)}
                    disabled={eventAlreadyStarted}
                  />
                </div>

                {/* Description En */}
                <div className="mb-3">
                  <label className="form-label">{t("event-page.event-page-create-descriptionEn")}</label>
                  <input
                    className="form-control"
                    value={descriptionEn}
                    onChange={(ev) => setDescriptionEn(ev.target.value)}
                    disabled={eventAlreadyStarted}
                  />
                </div>

                {/* Is Code Specified */}
                <div className="mb-3">
                  <input
                    type="checkbox"
                    id="isCodeSpecified"
                    onChange={() => setIsCodeSpecified(!isCodeSpecified)}
                    checked={isCodeSpecified}
                    disabled={eventAlreadyStarted}
                  />
                  <label htmlFor="isCodeSpecified" className="ms-2">
                    {t("event-page.event-has-code-specified")}
                  </label>
                </div>

                <hr />

                {/* Logo Url */}
                <div className="mb-3">
                  <label className="form-label">{t("event-page.event-page-create-logoUrl")}</label>
                  <input
                    className="form-control"
                    value={logoUrl}
                    onChange={(ev) => setLogoUrl(ev.target.value)}
                    disabled={eventAlreadyStarted}
                  />
                </div>

                {/* Price */}
                <div className="mb-3">
                  <label className="form-label">{t("event-page.event-page-create-price")}</label>
                  <input
                    type="number"
                    min="0.00"
                    step="0.01"
                    className="form-control"
                    value={price}
                    onChange={(ev) => onPriceChange(parseFloat(ev.target.value))}
                    required
                    disabled={eventAlreadyStarted}
                  />
                  {priceError && (
                    <label className="error-msg" id="price-Error-Msg">
                      {t("event-page.event-page-error-price")}
                    </label>
                  )}
                </div>

                {/* Sale Tax */}
                <div className="mb-3">
                  <label className="form-label">{t("event-page.event-page-create-saleTaxRate")}</label>

                  <div className="input-group mb-3">
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={saleTaxRate}
                      onChange={(ev) => onSaleTaxChange(ev.target.value)}
                      required
                      disabled={eventAlreadyStarted}
                    />

                    <div className="input-group-append">
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                  {saleTaxRateError && (
                    <label className="error-msg" id="saleTaxRate-Error-Msg">
                      {t("event-page.event-page-error-saleTaxRate")}
                    </label>
                  )}
                </div>

                {/* SizeId */}
                <div className="mb-3" id="size">
                  <label className="me-2">{t("event-page.event-page-locker-size")}</label>
                  <div className={`mb-3 ${eventAlreadyStarted && "readOnly"}`} id="sizeInfo">
                    <select
                      id={"sizeInfoSelect"}
                      className="form-select"
                      value={sizeId}
                      disabled={eventAlreadyStarted}
                      onChange={(ev) => setSizeId(ev.target.value)}
                    >
                      <option key="Select_a_size" id="Select_a_size" value="Select a size" hidden>
                        {t("event-page.event-page-no-size-selected")}
                      </option>
                      {micrositeCompartmentSizes &&
                        micrositeCompartmentSizes.map((compartmentSize: CompartmentSize) => {
                          return (
                            <option key={"sizeOpt_" + compartmentSize.id} id={"sizeOpt_" + compartmentSize.id} value={compartmentSize.id}>
                              {compartmentSize?.name.get(language)} - {compartmentSize?.description.get(language)}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                </div>

                <div className={isDurationInput ? "mb-3" : ""}>
                  <span>
                    <input
                      type="radio"
                      id="byDuration"
                      name="reserve"
                      value="duration"
                      checked={isDurationInput}
                      disabled={eventAlreadyStarted}
                      onChange={() => onIsDurationClick()}
                    />
                    <label htmlFor="byDuration" className="ms-2 me-3" onClick={() => onIsDurationClick}>
                      {t("event-page.event-page-create-reserveByDuration")}
                    </label>
                  </span>
                </div>

                {isDurationInput ? (
                  <>
                    {/*duration*/}
                    <div className={`ms-3 mb-3 ${eventAlreadyStarted && "readOnly"}`} id="duration">
                      <div>
                        <label className="form-label">{t("event-page.event-page-create-duration")}</label>
                        <span className="d-inline-block">
                          <label className="ms-2 me-2">{t("event-page.event-page-create-durationDays")}</label>
                          <NumberDropDown
                            id="durationDays"
                            min={0}
                            max={366}
                            current={durationDays}
                            disabled={eventAlreadyStarted}
                            onchange={onDurationChange}
                          />
                        </span>
                        <span className="d-inline-block">
                          <label className="ms-2 me-2">{t("event-page.event-page-create-durationHours")}</label>
                          <NumberDropDown
                            id="durationHours"
                            min={0}
                            max={24}
                            current={durationHours}
                            disabled={eventAlreadyStarted}
                            onchange={onDurationChange}
                          />
                        </span>
                        <span className="d-inline-block">
                          <label className="ms-2 me-2">{t("event-page.event-page-create-durationMinutes")}</label>
                          <NumberDropDown
                            id="durationMinutes"
                            min={0}
                            max={60}
                            current={durationMinutes}
                            disabled={eventAlreadyStarted}
                            onchange={onDurationChange}
                          />
                        </span>
                        {durationError && <label className="error-msg">{t("event-page.event-page-error-duration")}</label>}
                      </div>
                    </div>
                    <div className="ms-3 mb-3">
                      <input
                        type="checkbox"
                        id="withAvailableStartEnd"
                        onChange={onFixedPeriod}
                        checked={isFixedPeriod}
                        disabled={eventAlreadyStarted}
                      />
                      <label htmlFor="withAvailableStartEnd" className="ms-2">
                        {t("event-page.event-page-create-checkbox")}
                      </label>
                    </div>
                    {isFixedPeriod && (
                      <div id="fixedDates">
                        {/* Available Start */}
                        <div className="mb-3">
                          <label className="form-label">{t("event-page.event-page-create-availableStart")}</label>
                          <input
                            id="availableStartInput"
                            type="datetime-local"
                            className="form-control"
                            value={availableStart}
                            onChange={(ev) => onAvailableStartChange(ev.target.value)}
                            disabled={eventAlreadyStarted}
                          />
                          {availableStartError && <label className="error-msg">{t(availableStartError)}</label>}
                        </div>

                        {/* Available End */}
                        <div className="mb-3">
                          <label className="form-label">{t("event-page.event-page-create-availableEnd")}</label>
                          <input
                            id="availableEndInput"
                            type="datetime-local"
                            className="form-control"
                            value={availableEnd}
                            onChange={(ev) => onAvailableEndChange(ev.target.value)}
                            disabled={eventAlreadyStarted}
                          />
                          {availableEndError && <label className="error-msg">{t(availableEndError)}</label>}
                        </div>
                      </div>
                    )}
                    <div className="mb-3">
                      <input
                        type="radio"
                        id="byStartEnd"
                        name="reserve"
                        value="startend"
                        checked={!isDurationInput}
                        disabled={eventAlreadyStarted}
                        onChange={() => onIsDurationClick()}
                      />
                      <label htmlFor="byStartEnd" className="ms-2" onClick={() => onIsDurationClick}>
                        {t("event-page.event-page-create-reserveByStart-End")}
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <input
                        type="radio"
                        id="byStartEnd"
                        name="reserve"
                        value="startend"
                        checked={!isDurationInput}
                        disabled={eventAlreadyStarted}
                        onChange={() => onIsDurationClick()}
                      />
                      <label htmlFor="byStartEnd" className="ms-2" onClick={() => onIsDurationClick}>
                        {t("event-page.event-page-create-reserveByStart-End")}
                      </label>
                    </div>
                    <div id="datesOnly">
                      {/* Start On */}
                      <div className="mb-3">
                        <label className="form-label">{t("event-page.event-page-create-startOn")}</label>
                        <input
                          id="startOnInput"
                          type="datetime-local"
                          className="form-control"
                          value={startOn}
                          onChange={(ev) => onStartOnChange(ev.target.value)}
                          disabled={eventAlreadyStarted}
                        />
                        {startOnError && <label className="error-msg">{t(startOnError)}</label>}
                      </div>

                      {/* End On */}
                      <div className="mb-3">
                        <label className="form-label">{t("event-page.event-page-create-endOn")}</label>
                        <input
                          id="EndOnInput"
                          type="datetime-local"
                          className="form-control"
                          value={endOn}
                          onChange={(ev) => onEndOnChange(ev.target.value)}
                          disabled={eventAlreadyStarted}
                        />
                        {endOnError && <label className="error-msg">{t(endOnError)}</label>}
                      </div>

                      {/* Available Start */}
                      <div className="mb-3">
                        <label className="form-label">{t("event-page.event-page-create-availableStart")}</label>
                        <input
                          id="availableStartInput"
                          type="datetime-local"
                          className="form-control"
                          value={availableStart}
                          onChange={(ev) => onAvailableStartChange(ev.target.value)}
                          disabled={eventAlreadyStarted}
                        />
                        {availableStartError && <label className="error-msg">{t(availableStartError)}</label>}
                      </div>
                    </div>
                  </>
                )}
                <button
                  className="btn btn-md btn-primary"
                  type="submit"
                  onClick={() => createOrUpdateReservationBlock()}
                  disabled={!isValidData()}
                >
                  {t("common.save")}
                </button>

                <button
                  className="btn btn-md btn-primary-outline"
                  type="submit"
                  onClick={() => history.push(`/organizations/${organizationId}/microsites/${micrositeId}/reservationBlocks`)}
                >
                  {t("common.cancel")}
                </button>

                {eventId !== "new" && (
                  <button
                    className={`btn btn-outline-primary float-end`}
                    onClick={() => setIsDeleteReservationBlock(true)}
                    disabled={false}
                  >
                    <FontAwesomeIcon icon={["fas", "trash-alt"]} /> {t("common.delete")}
                  </button>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function convertSelectOptionToDurationInt() {
    var days: HTMLSelectElement = document.getElementById("durationDays") as HTMLSelectElement;
    var hours: HTMLSelectElement = document.getElementById("durationHours") as HTMLSelectElement;
    var minutes: HTMLSelectElement = document.getElementById("durationMinutes") as HTMLSelectElement;

    if (days && hours && minutes) {
      let daysTmp = parseInt(days.options[days.selectedIndex].value);
      let daysValue = daysTmp * 86400;

      let hoursTmp = parseInt(hours.options[hours.selectedIndex].value);
      let hoursValue = hoursTmp * 3600;

      let minsTmp = parseInt(minutes.options[minutes.selectedIndex].value);
      let minsValue = minsTmp * 60;

      return daysValue + hoursValue + minsValue;
    }
    return 0;
  }

  function isValidData(): boolean {
    if (hasError()) return false;

    if (eventAlreadyStarted || !nameFr || !nameEn || isNaN(price) || price < 0 || isNaN(saleTaxRate) || sizeId === undefined) return false;

    if (isDurationInput && !isDurationValid()) return false;
    else if (!isDurationInput && (!startOn || !endOn)) return false;

    if (isFixedPeriod && (!availableStart || !availableEnd)) return false;

    if (availableStart && availableEnd) return !(availableStart > availableEnd || dateTimeNow() > availableStart);

    if (startOn && endOn) return !(startOn > endOn || dateTimeNow() > startOn);

    return true;
  }

  function onNameChange(value: string, setValue: Function, setError: Function) {
    setValue(value);
    if (!value || value.length === 0) {
      setError(true);
    } else {
      setError(false);
    }
  }

  function onAvailableStartChange(value: string) {
    setAvailableStart(value);
    if (availableEnd && value) {
      if (dateTimeNow() > value) {
        setAvailableStartError("event-page.event-page-error-availableStart-now");
      } else if (value > availableEnd) {
        setAvailableStartError("event-page.event-page-error-availableStart-availableEnd");
      } else {
        setAvailableStartError(undefined);
      }
    } else {
      setAvailableStartError(undefined);
      setDurationError(false);
    }
  }

  function onAvailableEndChange(value: string) {
    setAvailableEnd(value);
    if (availableStart && value) {
      if (dateTimeNow() > value) {
        setAvailableEndError("event-page.event-page-error-availableEnd-now");
      } else if (availableStart > value) {
        setAvailableEndError("event-page.event-page-error-availableStart-availableEnd");
      } else {
        setAvailableEndError(undefined);
      }
    } else {
      setAvailableEndError(undefined);
      setDurationError(false);
    }
  }

  function onStartOnChange(value: string) {
    setStartOn(value);
    if (value && endOn) {
      if (dateTimeNow() > value) {
        setStartOnError("event-page.event-page-error-startOn-now");
      } else if (value > endOn) {
        setStartOnError("event-page.event-page-error-startOn-endOn");
      } else {
        setStartOnError(undefined);
      }
    } else {
      setStartOnError(undefined);
      setDurationError(false);
    }
  }

  function onEndOnChange(value: string) {
    setEndOn(value);
    setAvailableEnd(value);
    if (startOn && value) {
      if (dateTimeNow() > value) {
        setEndOnError("event-page.event-page-error-endOn-now");
      } else if (startOn > value) {
        setEndOnError("event-page.event-page-error-startOn-endOn");
      } else {
        setEndOnError(undefined);
      }
    } else {
      setEndOnError(undefined);
      setDurationError(false);
    }
  }

  function onPriceChange(value: number) {
    setPrice(value);
    if (value < 0) {
      setPriceError(true);
    } else {
      setPriceError(false);
    }
  }

  function onSaleTaxChange(value: string) {
    const parsedValue = parseFloat(value);
    setSaleTaxRate(parsedValue);
    if (parsedValue !== undefined) {
      setSaleTaxError(!isValidSaleTax(value));
    }
  }

  function onDurationChange(id: string) {
    var select: HTMLSelectElement = document.getElementById(id) as HTMLSelectElement;

    let tmp = parseInt(select.options[select.selectedIndex].value);

    if (id === "durationDays") {
      setDurationDays(tmp);
    } else if (id === "durationHours") {
      setDurationHours(tmp);
    } else if (id === "durationMinutes") {
      setDurationMinutes(tmp);
    }

    if (tmp === 0 && !isDurationValid()) setDurationError(true);
    else setDurationError(false);
  }

  function onIsDurationClick() {
    if (eventId === "new") {
      if (isDurationInput) {
        setStartOn(undefined);
        setEndOn(undefined);
        setAvailableStartError(undefined);
        setEndOnError(undefined);
      } else {
        setAvailableEnd(undefined);
      }
      setAvailableStart(undefined);
    }

    if (isDurationInput) {
      setDurationDays(0);
      setDurationHours(0);
      setDurationMinutes(0);
    } else {
      setStartOn(undefined);
    }

    setIsFixedPeriod(false);
    setIsDurationInput(!isDurationInput);
  }

  function onFixedPeriod() {
    if (isFixedPeriod) {
      setAvailableStartError(undefined);
      setAvailableEndError(undefined);
      if (eventId === "new") {
        setAvailableStart(undefined);
        setAvailableEnd(undefined);
      }
    }
    setIsFixedPeriod(!isFixedPeriod);
    setAvailableStart(undefined);
    setAvailableEnd(undefined);
  }

  function createOrUpdateReservationBlock() {
    let duration = 0;
    let availableEndValue = availableEnd ?? "";
    const availableStartValue = availableStart ?? "";
    const startOnValue = startOn ?? "";
    const lockerSize = sizeId?.toLowerCase();

    if (sizeId === undefined) return;

    if (durationDays || durationHours || durationMinutes) {
      duration = convertSelectOptionToDurationInt();
    } else if (endOn && startOn) {
      let durationInMilisec = Date.parse(endOn) - Date.parse(startOn);
      duration = Math.round(durationInMilisec / 1000);
      availableEndValue = endOn;
    }

    if (duration > 0) {
      let name = { fr: nameFr, en: nameEn };
      let description = { fr: descriptionFr, en: descriptionEn };
      let availableEnd = availableEndValue !== "" ? moment.utc(new Date(availableEndValue)).format("YYYY-MM-DDTHH:mm Z") : undefined;
      let availableStart = availableStartValue !== "" ? moment.utc(new Date(availableStartValue)).format("YYYY-MM-DDTHH:mm Z") : undefined;
      let startOn = startOnValue !== "" ? moment.utc(new Date(startOnValue)).format("YYYY-MM-DDTHH:mm Z") : undefined;

      let data = {
        Name: name,
        Description: description,
        LogoUrl: logoUrl,
        Price: price,
        SaleTaxRate: saleTaxRate / 100,
        StartOn: startOn,
        Duration: duration,
        AvailableStart: availableStart,
        AvailableEnd: availableEnd,
        SizeId: lockerSize,
        CodeSpecified: isCodeSpecified,
      };

      if (eventId === "new") createEvent(data);
      else updateEvent(data);
    }
  }

  function updateEvent(data: any) {
    data["Id"] = eventId;

    micrositeService
      .updateReservationBlock(organizationId, micrositeId, data)
      .then(() => {
        history.push(`/organizations/${organizationId}/microsites/${micrositeId}/reservationBlocks`);
      })
      .catch((error: any) => {
        if (error?.response?.status === 409) {
          ToastHelpers.error(t("event-page.event-page-error-update-reservationBlock"));
        } else {
          ToastHelpers.error(t("common.error-generic"));
        }
      });
  }

  function createEvent(data: any) {
    data["MicrositeId"] = micrositeId;
    data.Active = true;

    micrositeService
      .createReservationBlock(organizationId, data)
      .then(() => {
        return history.push(`/organizations/${organizationId}/microsites/${micrositeId}/reservationBlocks`);
      })
      .catch((error: any) => {
        if (error?.response?.status === 409) {
          ToastHelpers.error(t("event-page.event-page-error-create-reservationBlock"));
        } else {
          ToastHelpers.error(t("common.error-generic"));
        }
      });
  }

  function deleteReservationBlock() {
    if (organizationId && eventId !== "new") {
      setIsDeleteReservationBlock(false);
      micrositeService
        .deleteReservationBlock(organizationId, micrositeId, eventId)
        .then(() => {
          return history.push(`/organizations/${organizationId}/microsites/${micrositeId}/reservationBlocks`);
        })
        .catch((error: any) => {
          if (error?.response?.status === 409) {
            ToastHelpers.error(t("event-page.event-page-error-delete-reservationBlock"));
          } else {
            ToastHelpers.error(t("common.error-generic"));
          }
        });
    }
  }

  function hasError() {
    return (
      nameFrError ||
      nameEnError ||
      priceError ||
      durationError ||
      availableEndError ||
      availableStartError ||
      endOnError ||
      saleTaxRateError
    );
  }

  function isDurationValid() {
    return (
      (durationDays && durationDays >= 0) ||
      (durationHours && durationHours >= 0) ||
      (durationMinutes && durationMinutes >= 0) ||
      (isFixedPeriod && (!availableStart || !availableEnd))
    );
  }
}

function isValidSaleTax(value: string): boolean {
  const regex = new RegExp(/^[0-9]{1,2}$|^[0-9]{1,2}\.[0-9]{1,3}$/);
  return regex.test(value);
}
